'use client';

import React, { useCallback, useState, useMemo, memo, useRef } from 'react';
import { ScreenContent, ScreenTabBar, ScreenFixedTop, ScreenFixedBottom } from '../../screen';
import { ItemListItem } from '../../components/ItemListItem';
import { Button, ButtonGroup } from '../../components/ui';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { FilterModal } from './FilterModal';
import { ShopFiltersPanel, DEFAULT_FILTERS, type FilterValues } from './ShopFiltersPanel';
import {
  WEAPON_CATEGORIES,
  OTHER_CATEGORIES,
  CATEGORY_TO_DB_MAP,
  type WeaponItem,
  type OtherCategory,
} from './weaponCategories';
import translations from './ShopScreen.translations';
import { FaSlidersH, FaChevronDown, FaChevronUp } from '../../components/icons';

// Pre-compute static data outside component to avoid any re-computation
const WEAPON_ITEMS_MAP = new Map<string, { name: string; imageUrl: string; rarityColor: string }>();
for (const category of WEAPON_CATEGORIES) {
  for (const weapon of category.weapons) {
    WEAPON_ITEMS_MAP.set(weapon.id, {
      name: weapon.name,
      imageUrl: weapon.image,
      rarityColor: '#6b7280',
    });
  }
}

// Memoized grid component that NEVER re-renders during swipe animation
interface WeaponGridProps {
  categoryId: string;
  categoryNameKey: string;
  weapons: readonly WeaponItem[];
  onWeaponClick: (weaponId: string, categoryId: string) => void;
  t: (key: keyof typeof translations['en']) => string;
}

const VISIBLE_CATEGORY_ITEMS = 2;

const WeaponCategorySection = memo<WeaponGridProps>(({ categoryId, categoryNameKey, weapons, onWeaponClick, t }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? weapons : weapons.slice(0, VISIBLE_CATEGORY_ITEMS);
  return (
    <section key={categoryId} className="mb-6 last:mb-0">
      <h2 className="text-base font-semibold text-white mb-3 ps-1">{t(categoryNameKey as keyof typeof translations['en'])}</h2>
      <div className="grid grid-cols-2 gap-1">
        {visible.map((weapon) => (
          <ItemListItem
            key={weapon.id}
            className="flex flex-col items-center justify-center p-2 min-h-[90px] cursor-pointer"
            item={WEAPON_ITEMS_MAP.get(weapon.id)!}
            onClick={() => onWeaponClick(weapon.id, categoryId)}
          />
        ))}
      </div>
      {weapons.length > VISIBLE_CATEGORY_ITEMS && (
        <button
          className="mt-2 w-full h-[44px] rounded-full bg-bg-secondary text-label-secondary text-[14px] font-medium flex items-center justify-center gap-1.5 active:bg-fill-tertiary/40 transition-colors"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? t('categoriesHide') : t('categoriesShowAll')}
          {expanded ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
        </button>
      )}
    </section>
  );
});

interface OtherGridProps {
  categories: readonly OtherCategory[];
  itemsMap: Map<string, { name: string; imageUrl: string; rarityColor: string }>;
  onCategoryClick: (categoryId: string) => void;
  t: (key: keyof typeof translations['en']) => string;
}

const OtherCategoriesSection = memo<OtherGridProps>(({ categories, itemsMap, onCategoryClick, t }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? categories : categories.slice(0, VISIBLE_CATEGORY_ITEMS);
  return (
    <section className="mb-6 last:mb-0">
      <h2 className="text-base font-semibold text-white mb-3 ps-1">{t('categoriesOther')}</h2>
      <div className="grid grid-cols-2 gap-1">
        {visible.map((category) => (
          <ItemListItem
            key={category.id}
            className="flex flex-col items-center justify-center p-2 min-h-[90px] cursor-pointer"
            item={itemsMap.get(category.id)!}
            onClick={() => onCategoryClick(category.id)}
          />
        ))}
      </div>
      {categories.length > VISIBLE_CATEGORY_ITEMS && (
        <button
          className="mt-2 w-full h-[44px] rounded-full bg-bg-secondary text-label-secondary text-[14px] font-medium flex items-center justify-center gap-1.5 active:bg-fill-tertiary/40 transition-colors"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? t('categoriesHide') : t('categoriesShowAll')}
          {expanded ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
        </button>
      )}
    </section>
  );
});

interface ShopCategoriesScreenProps {
  onBack?: () => void;
}

const ShopCategoriesScreenComponent: React.FC<ShopCategoriesScreenProps> = memo(() => {
  const { t } = useLocalTranslation(translations);
  const isDesktop = useIsDesktop();
  const navigation = useNavigation();

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<FilterValues>(DEFAULT_FILTERS);

  // Use ref to store navigation to prevent handler re-creation during swipe
  const navigationRef = useRef(navigation);
  navigationRef.current = navigation;

  const handleApplyFilters = useCallback(
    (newFilters: FilterValues) => {
      setFilters(newFilters);
      setIsFilterModalOpen(false);

      navigationRef.current.navigate('shop-items', {
        category: newFilters.category || undefined,
        weapon: newFilters.weapon || undefined,
        type: newFilters.type || undefined,
        wear: newFilters.wear || undefined,
        rarity: newFilters.rarity || undefined,
        minPrice: newFilters.minPrice || undefined,
        maxPrice: newFilters.maxPrice || undefined,
        statTrak: newFilters.statTrak || undefined,
        sortBy: newFilters.sortBy || undefined,
        title: t('title'),
      });
    },
    [t]
  );

  // Stable handler that uses ref - NEVER changes, so memoized children won't re-render
  const handleWeaponClick = useCallback((weaponId: string, categoryId: string) => {
    const category = WEAPON_CATEGORIES.find(c => c.id === categoryId);
    const weapon = category?.weapons.find(w => w.id === weaponId);
    if (!weapon) return;

    const dbCategory = CATEGORY_TO_DB_MAP[categoryId];
    navigationRef.current.navigate('shop-skins', {
      weapon: weapon.name,
      category: dbCategory,
      weaponImage: weapon.image,
    });
  }, []);

  // Stable handler for other categories
  const handleOtherClick = useCallback((categoryId: string) => {
    const category = OTHER_CATEGORIES.find(c => c.id === categoryId);
    if (!category) return;

    navigationRef.current.navigate('shop-items', {
      type: category.type,
      title: category.name,
    });
  }, []);

  const handleOpenFilterModal = useCallback(() => {
    setIsFilterModalOpen(true);
  }, []);

  const handleCloseFilterModal = useCallback(() => {
    setIsFilterModalOpen(false);
  }, []);

  // OTHER_CATEGORIES items depend on translation function
  const otherItemsMap = useMemo(() => {
    const map = new Map<string, { name: string; imageUrl: string; rarityColor: string }>();
    for (const category of OTHER_CATEGORIES) {
      map.set(category.id, {
        name: t(category.nameKey),
        imageUrl: category.image,
        rarityColor: '#6b7280',
      });
    }
    return map;
  }, [t]);

  const categoriesContent = (
    <div className="contain-[layout_style]">
      {WEAPON_CATEGORIES.map((category) => (
        <WeaponCategorySection
          key={category.id}
          categoryId={category.id}
          categoryNameKey={category.nameKey}
          weapons={category.weapons}
          onWeaponClick={handleWeaponClick}
          t={t}
        />
      ))}
      <OtherCategoriesSection
        categories={OTHER_CATEGORIES}
        itemsMap={otherItemsMap}
        onCategoryClick={handleOtherClick}
        t={t}
      />
    </div>
  );

  if (isDesktop) {
    return (
      <ScreenContent scrollKey="shop-categories">
        <div className="flex gap-6 p-6">
          {/* Left: filters sidebar */}
          <div className="w-[260px] shrink-0 sticky top-6 self-start">
            <div
              className="rounded-2xl border border-white/[0.06] p-5 flex flex-col gap-2"
              style={{ background: 'linear-gradient(180deg, rgba(15,18,30,0.95) 0%, rgba(10,12,20,0.98) 100%)' }}
            >
              <h2 className="text-sm font-bold text-white m-0 mb-2">{t('title')}</h2>
              <ShopFiltersPanel filters={filters} onApply={handleApplyFilters} onReset={() => setFilters(DEFAULT_FILTERS)} />
            </div>
          </div>
          {/* Right: categories */}
          <div className="flex-1 min-w-0">
            {categoriesContent}
          </div>
        </div>
      </ScreenContent>
    );
  }

  return (
    <>
      <ScreenFixedTop title={t('title')} />
      <ScreenTabBar />
      <ScreenFixedBottom>
        <ButtonGroup>
          <Button
            size="l"
            before={<FaSlidersH />}
            onClick={handleOpenFilterModal}
            stretched
          >
            {t('showFilters')}
          </Button>
        </ButtonGroup>
      </ScreenFixedBottom>
      <ScreenContent scrollKey="shop-categories">{categoriesContent}</ScreenContent>

      <FilterModal
        isOpen={isFilterModalOpen}
        filters={filters}
        onApply={handleApplyFilters}
        onClose={handleCloseFilterModal}
      />
    </>
  );
});

export const ShopCategoriesScreen = ShopCategoriesScreenComponent;
